import React from 'react';
import { Box, ButtonBase, Tooltip, useTheme } from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import { useTranslation } from 'react-i18next';
import { useThemeMode, type ThemeMode } from '../contexts/ThemeContext';

interface Option {
  value: ThemeMode;
  tKey: string;
  icon: React.ReactNode;
}

export default function ThemeToggleButton() {
  const { mode, setMode } = useThemeMode();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useTranslation();

  const options: Option[] = [
    { value: 'light', tKey: 'theme.light', icon: <LightModeIcon fontSize="small" /> },
    { value: 'dark',  tKey: 'theme.dark',  icon: <DarkModeIcon  fontSize="small" /> },
    { value: 'auto',  tKey: 'theme.system', icon: <SettingsBrightnessIcon fontSize="small" /> },
  ];

  return (
    <Box
      role="group"
      aria-label={t('theme.switchLabel')}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        padding: '4px',
        borderRadius: '999px',
        border: '1px solid var(--border)',
        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
      }}
    >
      {options.map((opt) => {
        const active = mode === opt.value;
        return (
          <Tooltip key={opt.value} title={t(opt.tKey)} enterDelay={400}>
            <ButtonBase
              onClick={() => setMode(opt.value)}
              aria-pressed={active}
              component="button"
              sx={{
                width: 34,
                height: 30,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '999px',
                color: active ? 'var(--text)' : 'var(--text-subtle)',
                background: active
                  ? isDark
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(255,255,255,0.9)'
                  : 'transparent',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,.15)' : 'none',
                transition: 'background .25s ease, color .2s ease, box-shadow .25s ease',
                '&:hover': {
                  color: 'var(--text)',
                  background: active
                    ? isDark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.95)'
                    : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.04)',
                },
              }}
            >
              {opt.icon}
            </ButtonBase>
          </Tooltip>
        );
      })}
    </Box>
  );
}
